import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { WithTypeContaining } from "../../../types/utilityTypes";

export type HomeAddressState = {
  province: string;
  state: string;
  addressLine1: string;
  permanentAddressLine1: string;
  addressLine1Transliterated: string;
  addressLine2: string;
  permanentAddressLine2: string;
  addressLine2Transliterated: string;
  addressLine3: string;
  addressLine3Transliterated: string;
  city: string;
  permanentAddressCity: string;
  cityTransliterated: string;
  postalCode: string;
  permanentAddressPostalCode: string;
  district: string;
  permanentAddressProvince: string;
  subdistrict: string;
  otherMailingAddress?: boolean;
};

type HomeAddressAction = {
  setHomeAddress: (
    personalInfoValues: WithTypeContaining<HomeAddressState>
  ) => void;
  reset: () => void;
};

const initialState: HomeAddressState = {
  province: "",
  state: "",
  addressLine1: "",
  addressLine1Transliterated: "",
  permanentAddressLine1: "",
  addressLine2: "",
  addressLine2Transliterated: "",
  addressLine3: "",
  addressLine3Transliterated: "",
  permanentAddressLine2: "",
  city: "",
  cityTransliterated: "",
  permanentAddressCity: "",
  postalCode: "",
  permanentAddressPostalCode: "",
  district: "",
  permanentAddressProvince: "",
  subdistrict: "",
  otherMailingAddress: false,
};

export const useHomeAddressStore = create<
  HomeAddressState & HomeAddressAction
>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,
        setHomeAddress: (personalInfoValues) => {
          set(createHomeAddressFromForm(personalInfoValues));
        },
        reset: () => set(initialState),
      }),
      {
        name: "home-address-storage",
        storage: createJSONStorage(() => sessionStorage),
      }
    )
  )
);

export const homeAddressFromStore = () => {
  return {
    province: useHomeAddressStore.getState().province,
    state: useHomeAddressStore.getState().state,
    addressLine1: useHomeAddressStore.getState().addressLine1,
    addressLine1Transliterated:
      useHomeAddressStore.getState().addressLine1Transliterated,
    permanentAddressLine1: useHomeAddressStore.getState().permanentAddressLine1,
    addressLine2: useHomeAddressStore.getState().addressLine2,
    addressLine2Transliterated:
      useHomeAddressStore.getState().addressLine2Transliterated,
    addressLine3: useHomeAddressStore.getState().addressLine3,
    addressLine3Transliterated:
      useHomeAddressStore.getState().addressLine3Transliterated,
    permanentAddressLine2: useHomeAddressStore.getState().permanentAddressLine2,
    city: useHomeAddressStore.getState().city,
    cityTransliterated: useHomeAddressStore.getState().cityTransliterated,
    permanentAddressCity: useHomeAddressStore.getState().permanentAddressCity,
    postalCode: useHomeAddressStore.getState().postalCode,
    permanentAddressPostalCode:
      useHomeAddressStore.getState().permanentAddressPostalCode,
    district: useHomeAddressStore.getState().district,
    permanentAddressProvince:
      useHomeAddressStore.getState().permanentAddressProvince,
    subdistrict: useHomeAddressStore.getState().subdistrict,
    otherMailingAddress: useHomeAddressStore.getState().otherMailingAddress,
  };
};

export const createHomeAddressFromForm = ({
  province,
  addressLine1,
  addressLine1Transliterated,
  permanentAddressLine1,
  addressLine2,
  addressLine2Transliterated,
  addressLine3,
  addressLine3Transliterated,
  permanentAddressLine2,
  city,
  cityTransliterated,
  permanentAddressCity,
  postalCode,
  permanentAddressPostalCode,
  district,
  permanentAddressProvince,
  subdistrict,
  state,
  otherMailingAddress,
}: WithTypeContaining<HomeAddressState>): HomeAddressState => ({
  province,
  addressLine1,
  addressLine1Transliterated,
  permanentAddressLine1,
  addressLine2,
  addressLine2Transliterated,
  addressLine3,
  addressLine3Transliterated,
  permanentAddressLine2,
  city,
  cityTransliterated,
  permanentAddressCity,
  postalCode: postalCode.toUpperCase(),
  permanentAddressPostalCode: permanentAddressPostalCode.toUpperCase(),
  district,
  permanentAddressProvince,
  subdistrict,
  state,
  otherMailingAddress,
});
